import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserContextService } from '../../core/auth/user-context.service';
import { CollectionPage, InMemoryDataService } from '../../core/data/in-memory-data.service';
import { EntityConfig, getEntityConfig } from '../../core/config/entity-config';
import { resolveDefaultRateForCategoryName } from '../../core/config/instructor-rates.config';
import {
  normalizeResourceSubType,
  normalizeResourceType,
  RESOURCE_SUB_TYPE_OPTIONS,
  RESOURCE_TYPE_OPTIONS
} from '../../core/config/resource-enums.config';
import { CATEGORY_DICTIONARY } from '../../core/data/category-dictionary';
import { CollectionKey } from '../../core/models/domain.models';
import { formatPln } from '../../core/utils/currency.util';
import { dateKey, formatDisplayDate, formatDisplayTime } from '../../core/utils/date-format.util';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { AssignRelationDialogComponent, AssignOption } from '../resource-details/assign-relation-dialog.component';
import { AddPaymentDialogComponent } from '../resource-details/add-payment-dialog.component';
import { CancelLessonDialogComponent } from '../resource-details/cancel-lesson-dialog.component';
import { DeleteLessonDialogComponent } from '../../modules/instructor/lesson-correction-dialog.component';
import { FilterDialogComponent, FilterDialogData, FilterFieldConfig, FilterSelectOption } from './filter-dialog.component';
import { AddLessonDialogComponent } from './add-lesson-dialog.component';
import { CreateResourceDialogComponent } from './create-resource-dialog.component';
import { EditResourceDialogComponent } from './edit-resource-dialog.component';

type TableRow = Record<string, unknown> & { id: string | number; status: string };
const EMPTY_TABLE_PAGE: CollectionPage<TableRow> = {
  page: 1,
  pageSize: 10,
  total: 0,
  items: []
};

interface StatusAction {
  nextStatus: string;
  labelKey: string;
  icon: string;
}

interface RowQuickAction {
  key:
    | 'assignStudentCourse'
    | 'assignStudentInstructor'
    | 'assignInstructorCourse'
    | 'assignInstructorResource'
    | 'assignResourceCourse'
    | 'inviteInstructor'
    | 'deleteLesson';
  labelKey: string;
  icon: string;
}

interface InstructorResourceAssignmentOption extends AssignOption {
  resourceId: string | number;
}

interface ConfirmStatusDialogData {
  actionLabel: string;
  showDateCompleted?: boolean;
}

interface ConfirmStatusDialogResult {
  confirmed: boolean;
  dateCompleted?: string;
}

interface MonthlyInstructorLessonsView {
  totalHours: number;
  breakdown: Array<{ category: string; hours: number }>;
  monthLabel?: string;
}

@Component({
  selector: 'app-confirm-status-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, TranslatePipe],
  template: `
    <h2 mat-dialog-title>{{ 'statusConfirm.title' | t }}</h2>
    <mat-dialog-content>
      {{ 'statusConfirm.message' | t: { action: data.actionLabel } }}
      @if (data.showDateCompleted) {
        <form [formGroup]="form" class="confirm-status-form">
          <mat-form-field>
            <mat-label>{{ 'field.dateCompleted' | t }}</mat-label>
            <input matInput type="date" formControlName="dateCompleted" />
          </mat-form-field>
        </form>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close({ confirmed: false })">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button color="primary" (click)="confirm()">{{ 'statusConfirm.confirm' | t }}</button>
    </mat-dialog-actions>
  `,
  styles: `
    .confirm-status-form {
      margin-top: 16px;
      display: grid;
    }
  `
})
export class ConfirmStatusDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ConfirmStatusDialogComponent, ConfirmStatusDialogResult>);
  readonly data = inject<ConfirmStatusDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  readonly form = this.fb.group({
    dateCompleted: [this.todayIso()]
  });

  confirm(): void {
    if (this.data.showDateCompleted) {
      this.dialogRef.close({
        confirmed: true,
        dateCompleted: this.form.controls.dateCompleted.value || this.todayIso()
      });
      return;
    }
    this.dialogRef.close({ confirmed: true });
  }

  private todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }
}

@Component({
  selector: 'app-resource-list-page',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatMenuModule,
    MatChipsModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatTooltipModule,
    TranslatePipe
  ],
  templateUrl: './resource-list-page.component.html',
  styleUrl: './resource-list-page.component.scss'
})
export class ResourceListPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly dataService = inject(InMemoryDataService);
  private readonly userContext = inject(UserContextService);
  private readonly localization = inject(LocalizationService);
  private readonly paramMap = toSignal(this.route.paramMap, { initialValue: this.route.snapshot.paramMap });

  readonly pageIndex = signal(0);
  readonly pageSize = signal(10);
  readonly filter = signal<FilterDialogData>(this.buildDefaultFilter());
  readonly listLoading = signal(false);
  readonly listRefresh = signal(0);

  readonly entity = computed<EntityConfig | null>(() => {
    const key = this.paramMap().get('entityKey');
    return getEntityConfig(key) ?? null;
  });
  private readonly listPage = toSignal(this.buildListPageStream(), { initialValue: EMPTY_TABLE_PAGE });
  readonly rows = computed(() => this.listPage().items);
  readonly totalRows = computed(() => this.listPage().total);
  readonly entityTitle = computed(() => {
    const config = this.entity();
    if (!config) {
      return '';
    }
    const translated = this.localization.translate(`entity.${config.key}`);
    return translated === `entity.${config.key}` ? config.title : translated;
  });
  readonly entityAddLabel = computed(() => {
    const config = this.entity();
    if (!config) {
      return '';
    }
    const translated = this.localization.translate(config.addLabelKey);
    return translated === config.addLabelKey ? config.title : translated;
  });

  readonly pagedRows = computed(() => this.rows());

  readonly displayedColumns = computed(() => {
    const config = this.entity();
    if (!config) {
      return [];
    }
    const columns = config.columns.map((item) => item.key);
    if (this.hasActionsColumn(config.key)) {
      columns.push('actions');
    }
    return columns;
  });

  readonly filterChips = computed(() => {
    const chips: string[] = [];
    const currentFilter = this.filter();
    if (currentFilter.status) {
      chips.push(`${this.localization.translate('common.status')}: ${this.translateStatus(currentFilter.status)}`);
    }
    if (currentFilter.dateFrom) {
      chips.push(`${this.localization.translate('common.from')}: ${currentFilter.dateFrom}`);
    }
    if (currentFilter.dateTo) {
      chips.push(`${this.localization.translate('common.to')}: ${currentFilter.dateTo}`);
    }
    if (currentFilter.studentId) {
      chips.push(`${this.localization.translate('common.student')}: ${this.resolveReferenceValue('studentId', currentFilter.studentId)}`);
    }
    if (currentFilter.instructorId) {
      chips.push(
        `${this.localization.translate('common.instructor')}: ${this.resolveReferenceValue('instructorId', currentFilter.instructorId)}`
      );
    }
    if (currentFilter.categoryId) {
      chips.push(`${this.localization.translate('common.category')}: ${this.resolveReferenceValue('categoryId', currentFilter.categoryId)}`);
    }
    Object.entries(currentFilter.apiFilters ?? {})
      .filter(([, value]) => `${value ?? ''}`.trim().length > 0)
      .forEach(([key, value]) => {
        const field = this.apiFilterFieldsForEntity(this.entity()?.key).find((item) => item.value === key);
        const optionLabel = field?.options?.find((option) => `${option.value}` === `${value}`)?.label;
        chips.push(`${field?.label ?? key}: ${optionLabel ?? value}`);
      });
    Object.entries(currentFilter.columnFilters ?? {})
      .filter(([, value]) => value.trim().length > 0)
      .forEach(([key, value]) => {
        const field = this.filterFieldsForCurrentEntity().find((item) => item.value === key);
        chips.push(`${field?.label ?? key}: ${value}`);
      });
    return chips;
  });

  readonly canAddCurrentResource = computed(() => {
    const key = this.entity()?.key;
    if (key === 'lessons') {
      return this.userContext.canAddLesson();
    }
    if (key === 'payments') {
      return this.userContext.canAddPayment();
    }
    return this.canEditEntities();
  });
  readonly canAddLesson = this.userContext.canAddLesson;
  readonly canAddPayment = this.userContext.canAddPayment;
  readonly canEditEntities = this.userContext.canEditResources;
  readonly canAssignStudentRelations = this.userContext.canAssignStudentRelations;

  constructor() {
    effect(() => {
      const entity = this.entity();
      const defaultFilter = untracked(() => this.buildDefaultFilter(entity?.key, entity?.statuses ?? []));
      const restored = untracked(() => this.restoreListState(entity, defaultFilter));
      this.pageIndex.set(restored.pageIndex);
      this.pageSize.set(restored.pageSize);
      this.filter.set(restored.filter);
    });

    effect(() => {
      const entity = this.entity();
      if (!entity) {
        this.listLoading.set(false);
      }
    });
  }

  private buildListPageStream(): Observable<CollectionPage<TableRow>> {
    return combineLatest([
      toObservable(this.entity),
      toObservable(this.pageIndex),
      toObservable(this.pageSize),
      toObservable(this.filter),
      toObservable(this.listRefresh)
    ]).pipe(
      debounceTime(120),
      switchMap(([entity, pageIndex, pageSize, filter]) => {
        if (!entity) {
          return of({ ...EMPTY_TABLE_PAGE, pageSize });
        }
        this.listLoading.set(true);
        return this.dataService
          .fetchCollectionPage$(entity.key, {
            page: pageIndex + 1,
            pageSize,
            filters: this.buildListRequestFilters(entity, filter)
          })
          .pipe(
            map((page) => ({ ...page, items: page.items as unknown as TableRow[] })),
            switchMap((page) =>
              this.loadPageReferences$(entity, page.items).pipe(
                map(() => page),
                catchError(() => of(page))
              )
            ),
            catchError(() => of({ ...EMPTY_TABLE_PAGE, page: pageIndex + 1, pageSize })),
            finalize(() => this.listLoading.set(false))
          );
      })
    );
  }

  private buildListRequestFilters(entity: EntityConfig, filter: FilterDialogData): Record<string, unknown> {
    const params: Record<string, unknown> = {};
    const defaultStatus = filter.entityKey === entity.key ? '' : this.defaultStatusFilter(entity.key);
    const status = filter.status || defaultStatus;
    if (status) {
      params['status'] = status;
    }
    const query = this.buildBackendQuery(filter);
    if (query) {
      params['query'] = query;
    }
    Object.entries(filter.apiFilters ?? {}).forEach(([key, value]) => {
      if (`${value ?? ''}`.trim().length > 0) {
        params[key] = value;
      }
    });
    if (entity.key === 'lessons' && Object.keys(filter.apiFilters ?? {}).length === 0) {
      params['dateFrom'] = filter.dateFrom;
      params['dateTo'] = filter.dateTo;
      params['studentId'] = filter.studentId;
      params['instructorId'] = filter.instructorId;
      params['category'] = filter.categoryId;
    }
    if (entity.key === 'payments' && Object.keys(filter.apiFilters ?? {}).length === 0) {
      params['studentId'] = filter.studentId;
    }
    return params;
  }

  private buildBackendQuery(filter: FilterDialogData): string {
    return Object.entries(filter.columnFilters ?? {})
      .filter(([key]) => key !== 'paymentStatus' && key !== 'accountStatus' && key !== 'monthlyLessonHours')
      .map(([, value]) => value.trim())
      .filter((value) => value.length > 0)
      .join(' ');
  }

  private loadPageReferences$(entity: EntityConfig, rows: TableRow[]): Observable<void> {
    const references = this.referenceFieldsForEntity(entity.key).map((reference) => ({
      ...reference,
      rows: this.rowsMissingReferenceText(entity.key, rows, reference.field)
    }));
    if (references.length === 0 || rows.length === 0) {
      return of(undefined);
    }
    const loaders = references
      .filter((reference) => reference.rows.length > 0)
      .map(({ field, collection, rows: referenceRows }) =>
        this.dataService.ensureEntitiesLoaded$(
          collection,
          referenceRows
            .map((row) => row[field])
            .filter((id) => id != null && `${id}`.trim().length > 0)
        )
      );
    return loaders.length > 0 ? forkJoin(loaders).pipe(map(() => undefined)) : of(undefined);
  }

  private rowsMissingReferenceText(entityKey: CollectionKey, rows: TableRow[], field: string): TableRow[] {
    if (entityKey !== 'lessons') {
      return rows;
    }
    return rows.filter((row) => !this.embeddedReferenceValue(row, field));
  }

  private referenceFieldsForEntity(entityKey: CollectionKey): Array<{ field: string; collection: CollectionKey }> {
    if (entityKey === 'lessons') {
      return [
        { field: 'studentId', collection: 'students' },
        { field: 'instructorId', collection: 'instructors' },
        { field: 'resourceId', collection: 'resources' },
        { field: 'courseInstanceId', collection: 'courseInstances' }
      ];
    }
    if (entityKey === 'payments') {
      return [
        { field: 'studentId', collection: 'students' },
        { field: 'courseInstanceId', collection: 'courseInstances' }
      ];
    }
    if (entityKey === 'courseInstances') {
      return [{ field: 'courseId', collection: 'courses' }];
    }
    if (entityKey === 'organisations') {
      return [
        { field: 'addressId', collection: 'addresses' },
        { field: 'contactId', collection: 'contacts' }
      ];
    }
    return [];
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  openFilter(): void {
    const entity = this.entity();
    if (!entity) {
      return;
    }
    this.listLoading.set(true);
    this.ensureFilterReferences$(entity.key)
      .pipe(finalize(() => this.listLoading.set(false)))
      .subscribe(() => this.openFilterDialog(entity));
  }

  private openFilterDialog(entity: EntityConfig): void {
    const lessonsFilterOptions = entity.key === 'lessons' ? this.getLessonFilterOptions() : this.getEmptyLessonFilterOptions();
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      data: {
        ...this.filter(),
        entityKey: entity.key,
        title: this.filterTitleForEntity(entity),
        filterFields: this.filterFieldsForCurrentEntity(entity),
        apiFilterFields: this.apiFilterFieldsForEntity(entity.key),
        statusOptions: entity.statuses ?? [],
        ...lessonsFilterOptions
      },
      width: '1200px',
      maxWidth: '96vw'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.filter.set(result);
      this.pageIndex.set(0);
    });
  }

  private ensureFilterReferences$(entityKey: EntityConfig['key']): Observable<void> {
    const keys = this.filterReferenceCollections(entityKey).filter((key) => this.isReferenceCollectionEmpty(key));
    if (keys.length === 0) {
      return of(undefined);
    }
    return forkJoin(
      keys.map((key) =>
        this.dataService.fetchCollectionPage$(key, { page: 1, pageSize: 100 }).pipe(catchError(() => of(null)))
      )
    ).pipe(map(() => undefined));
  }

  private filterReferenceCollections(entityKey: EntityConfig['key']): CollectionKey[] {
    const keysByEntity: Partial<Record<EntityConfig['key'], CollectionKey[]>> = {
      students: ['instructors', 'courseInstances'],
      instructors: ['students', 'courseInstances'],
      lessons: ['students', 'instructors', 'resources', 'courseInstances', 'courses'],
      payments: ['students', 'courseInstances'],
      courseInstances: ['courses', 'students', 'instructors', 'resources'],
      resources: ['courses', 'courseInstances', 'instructors']
    };
    return keysByEntity[entityKey] ?? [];
  }

  private isReferenceCollectionEmpty(key: CollectionKey): boolean {
    switch (key) {
      case 'students':
        return this.dataService.students().length === 0;
      case 'instructors':
        return this.dataService.instructors().length === 0;
      case 'resources':
        return this.dataService.resources().length === 0;
      case 'courseInstances':
        return this.dataService.courseInstances().length === 0;
      case 'courses':
        return this.dataService.courses().length === 0;
      default:
        return false;
    }
  }

  clearFilters(): void {
    const current = this.filter();
    this.filter.set({
      ...current,
      status: '',
      apiFilters: {},
      columnFilters: {},
      dateFrom: '',
      dateTo: '',
      studentId: '',
      instructorId: '',
      categoryId: ''
    });
    this.pageIndex.set(0);
  }

  shouldShowStatusQuickFilters(): boolean {
    const key = this.entity()?.key;
    return key === 'students' || key === 'instructors' || key === 'payments' || key === 'lessons';
  }

  quickStatusOptions(): string[] {
    if (!this.shouldShowStatusQuickFilters()) {
      return [];
    }
    return this.entity()?.statuses ?? [];
  }

  setQuickStatusFilter(status: string): void {
    this.filter.update((current) => ({ ...current, status }));
    this.pageIndex.set(0);
  }

  isQuickStatusActive(status: string): boolean {
    return this.filter().status === status;
  }

  openAddResource(): void {
    const config = this.entity();
    if (!config) {
      return;
    }
    if (!this.canAddCurrentResource()) {
      return;
    }

    if (config.key === 'lessons') {
      const dialogRef = this.dialog.open(AddLessonDialogComponent, {
        width: '980px',
        maxWidth: '96vw',
        data: {
          students: this.dataService.students(),
          instructors: this.dataService.instructors(),
          courseInstances: this.dataService.courseInstances(),
          resources: this.dataService.resources(),
          allowFuturePlanning: this.userContext.canPlanFutureLessons()
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (!result?.['created']) {
          return;
        }
        this.listRefresh.update((value) => value + 1);
      });
      return;
    }

    if (config.key === 'payments') {
      const dialogRef = this.dialog.open(AddPaymentDialogComponent, {
        width: '880px',
        maxWidth: '96vw',
        data: {
          students: this.dataService.students(),
          courseInstances: this.dataService.courseInstances(),
          addedByUserId: this.userContext.currentUser().id,
          addedByDisplay: this.userContext.currentUser().displayName
        }
      });

      dialogRef.afterClosed().subscribe((payload) => {
        if (!payload) {
          return;
        }
        void this.createResourceAndRefresh('payments', this.withEntityDefaults('payments', payload));
      });
      return;
    }

    const dialogRef = this.dialog.open(CreateResourceDialogComponent, {
      data: {
        entity: config,
        selectOptions: this.buildSelectOptions(config.key),
        courseTypes: Object.fromEntries(this.dataService.courses().map((course) => [String(course.id), course.courseType ?? 'STANDARD']))
      },
      width: config.key === 'instructors' ? '1180px' : '1120px',
      maxHeight: '95vh',
      maxWidth: '96vw'
    });

    dialogRef.afterClosed().subscribe((payload) => {
      if (!payload) {
        return;
      }
      void this.createResourceAndRefresh(config.key, this.withEntityDefaults(config.key, payload));
    });
  }

  private async createResourceAndRefresh(entityKey: EntityConfig['key'], payload: Record<string, unknown>): Promise<void> {
    this.listLoading.set(true);
    try {
      await this.dataService.createDynamicFromBackend(entityKey, payload);
      this.listRefresh.update((value) => value + 1);
    } catch {
      this.listLoading.set(false);
    }
  }

  openDetails(rowId: string | number): void {
    const config = this.entity();
    if (!config) {
      return;
    }
    this.router.navigate(['/resources', config.key, rowId], { queryParams: this.listStateQueryParams(config.key) });
  }

  canLinkToDetails(row: TableRow, key: string): boolean {
    return this.getLinkedTargetForCell(row, key) !== null;
  }

  getLinkedExternalReference(row: TableRow, key: string): string {
    const target = this.getLinkedTargetForCell(row, key);
    if (!target) {
      return '';
    }
    const embeddedPrefix = target.entityKey === 'students' ? 'student' : target.entityKey === 'instructors' ? 'instructor' : 'resource';
    const embedded = row[embeddedPrefix];
    const embeddedReference = embedded && typeof embedded === 'object' && !Array.isArray(embedded)
      ? (embedded as Record<string, unknown>)['externalReference']
      : null;
    const directReference = this.firstText(
      row[`${embeddedPrefix}ExternalReference`],
      embeddedReference,
      (key === 'name' || key === 'surname' || key === 'fullName') ? row['externalReference'] : null
    );
    const collection = target.entityKey === 'students'
      ? this.dataService.students()
      : target.entityKey === 'instructors'
        ? this.dataService.instructors()
        : this.dataService.resources();
    const linkedReference = collection.find((item) => this.sameId(item.id, target.id))?.externalReference ?? '';
    const value = directReference || linkedReference;
    return value ? `${this.localization.translate('field.externalReference')}: ${value}` : '';
  }

  openLinkedDetails(row: TableRow, key: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const target = this.getLinkedTargetForCell(row, key);
    if (!target) {
      return;
    }
    this.router.navigate(['/resources', target.entityKey, target.id], { queryParams: this.listStateQueryParams(target.entityKey) });
  }

  private listStateQueryParams(entityKey: EntityConfig['key']): Record<string, string> {
    const filter = this.filter();
    return {
      listState: JSON.stringify({
        entityKey,
        pageIndex: this.pageIndex(),
        pageSize: this.pageSize(),
        filter: {
          entityKey: filter.entityKey,
          status: filter.status,
          apiFilters: Object.fromEntries(
            Object.entries(filter.apiFilters).filter(([key]) => key !== 'personalIdentificationNumber')
          ),
          columnFilters: filter.columnFilters,
          dateFrom: filter.dateFrom,
          dateTo: filter.dateTo,
          studentId: filter.studentId,
          instructorId: filter.instructorId,
          categoryId: filter.categoryId
        }
      })
    };
  }

  private restoreListState(
    entity: EntityConfig | null,
    defaultFilter: FilterDialogData
  ): { pageIndex: number; pageSize: number; filter: FilterDialogData } {
    const fallback = { pageIndex: 0, pageSize: 10, filter: defaultFilter };
    const raw = this.route.snapshot.queryParamMap.get('listState');
    if (!entity || !raw) {
      return fallback;
    }
    try {
      const state = JSON.parse(raw) as {
        entityKey?: string;
        pageIndex?: unknown;
        pageSize?: unknown;
        filter?: Partial<FilterDialogData>;
      };
      if (state.entityKey !== entity.key || !state.filter || state.filter.entityKey !== entity.key) {
        return fallback;
      }
      const pageIndex = Number(state.pageIndex);
      const pageSize = Number(state.pageSize);
      return {
        pageIndex: Number.isInteger(pageIndex) && pageIndex >= 0 ? pageIndex : 0,
        pageSize: [5, 10, 20, 50].includes(pageSize) ? pageSize : 10,
        filter: {
          ...defaultFilter,
          ...state.filter,
          apiFilters: { ...(state.filter.apiFilters ?? {}) },
          columnFilters: { ...(state.filter.columnFilters ?? {}) },
          filterFields: this.filterFieldsForCurrentEntity(entity),
          apiFilterFields: this.apiFilterFieldsForEntity(entity.key),
          statusOptions: entity.statuses ?? []
        }
      };
    } catch {
      return fallback;
    }
  }

  getValue(row: TableRow, key: string): string {
    if (key === 'fullName') {
      return `${row['name'] ?? ''} ${row['surname'] ?? ''}`.trim();
    }
    if (key === 'courseProgress') {
      const progress = this.getCourseProgressView(row);
      return progress ? `${progress.name} ${progress.percent}% ${progress.completedHours}/${progress.requiredHours}` : '';
    }
    if (key === 'monthlyLessonHours') {
      const view = this.getMonthlyInstructorLessonsView(row);
      return view ? this.formatHours(view.totalHours) : '';
    }
    if (key === 'cancellationSummary') {
      return this.formatCancellationSummary(row);
    }
    const value = row[key];
    if (value == null) {
      if (key === 'addressId') {
        return this.formatEmbeddedAddress(row['address']);
      }
      return '';
    }

    if (key === 'status') {
      const entityKey = this.entity()?.key;
      if (!entityKey) {
        return this.translateStatus(String(value));
      }
      if (entityKey === 'payments' && this.isInstructorCashPayment(row) && this.getEffectiveStatusForList(entityKey, row) === 'pendingSettlement') {
        return this.localization.translate('payment.cashPendingOfficeSettlement');
      }
      return this.translateStatus(this.getEffectiveStatusForList(entityKey, row));
    }

    if (key === 'paymentType') {
      const translated = this.localization.translate(`common.paymentType.${String(value)}`);
      return translated === `common.paymentType.${String(value)}` ? String(value) : translated;
    }

    if (key === 'courseType') {
      return this.localization.translate(value === 'AD_HOC' ? 'courseType.adHoc' : 'courseType.standard');
    }

    if (key === 'billingModel') {
      return this.localization.translate(value === 'HOURLY' ? 'billingModel.hourly' : 'billingModel.fixedPrice');
    }

    if (key === 'paymentStatus') {
      return this.getPaymentStatusLabel(row);
    }

    if (key === 'accountStatus') {
      return this.getAccountStatusLabel(row);
    }

    if (key === 'type') {
      return this.translateResourceType(value);
    }

    if (key === 'subType') {
      return this.translateResourceSubType(value);
    }

    if (this.isMoneyKey(key)) {
      return this.formatMoney(value);
    }

    if (this.isDateKey(key)) {
      return this.formatDate(value);
    }

    if (key === 'lessonTime') {
      return formatDisplayTime(value);
    }

    if (key === 'durationMinutes') {
      return `${Number((Number(value) / 60).toFixed(1))} ${this.localization.translate('common.hoursShort')}`;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.resolveReferenceValue(key, item)).join(', ');
    }

    if (key === 'address') {
      return this.formatEmbeddedAddress(value);
    }

    if (key === 'country') {
      return this.formatCountry(value);
    }

    const embeddedReference = this.embeddedReferenceValue(row, key);
    if (embeddedReference) {
      return embeddedReference;
    }

    if (key.endsWith('Id')) {
      return this.resolveReferenceValue(key, value);
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return `${value ?? ''}`;
  }

  private getSearchableColumnValue(row: TableRow, key: string): string {
    return this.getValue(row, key).toLowerCase();
  }

  private matchesColumnFilter(config: EntityConfig, row: TableRow, columnKey: string, filterValue: string): boolean {
    if (this.getFilterFieldType(config, columnKey) === 'select') {
      return `${row[columnKey] ?? ''}` === filterValue;
    }
    if (this.getFilterFieldType(config, columnKey) === 'date') {
      return dateKey(row[columnKey]) === filterValue;
    }
    return this.getSearchableColumnValue(row, columnKey).includes(filterValue.toLowerCase());
  }

  getColumnLabel(columnKey: string, fallback: string): string {
    if (columnKey === 'actions') {
      const translatedActions = this.localization.translate('common.actions');
      return translatedActions === 'common.actions' ? 'Akcje' : translatedActions;
    }
    const keyMap: Record<string, string> = {
      status: 'common.status',
      student: 'common.student',
      studentId: 'common.student',
      instructor: 'common.instructor',
      instructorId: 'common.instructor',
      resource: 'common.resource',
      resourceId: 'common.resource',
      course: 'common.course',
      categoryId: 'common.category',
      category: 'common.category',
      categories: 'common.category',
      courseId: this.entity()?.key === 'courseInstances' ? 'common.product' : 'common.course',
      durationMinutes: 'common.duration',
      fullName: this.entity()?.key === 'instructors' ? 'common.instructor' : 'common.student',
      monthlyLessonHours: 'field.monthlyLessonHours',
      courseProgress: 'resourceDetails.courseProgress',
      paymentStatus: 'field.paymentStatus',
      paymentType: 'paymentDialog.paymentType',
      amount: 'paymentDialog.amount'
    };
    if (columnKey === 'monthlyLessonHours') {
      return this.getMonthlyLessonHoursColumnLabel(keyMap[columnKey] ?? fallback);
    }
    const translationKey = keyMap[columnKey] ?? `field.${columnKey}`;
    const translated = this.localization.translate(translationKey);
    return translated === translationKey ? this.translateFallbackLabel(fallback) : translated;
  }

  private getMonthlyLessonHoursColumnLabel(labelKey: string): string {
    const translated = this.localization.translate(labelKey);
    const baseLabel = translated === labelKey ? this.translateFallbackLabel(labelKey) : translated;
    const month = this.monthYearLabel(this.firstMonthlyLessonHoursMonth());
    if (!month) {
      return baseLabel;
    }
    const hours = this.localization.translate('common.hoursShort');
    const labelWithoutHours = baseLabel.replace(/\s*\([^)]*\)\s*$/, '').trim();
    return `${labelWithoutHours} (${hours}, ${month})`;
  }

  private firstMonthlyLessonHoursMonth(): string {
    return this.firstText(...this.rows().map((row) => row['monthlyLessonHoursMonth']));
  }

  getAccountStatusLabel(row: TableRow): string {
    if (row['credentialsActive'] === true) {
      return this.localization.translate('accountStatus.credentialsActive');
    }
    if (row['invited'] === true) {
      return this.localization.translate('accountStatus.invited');
    }
    return this.localization.translate('accountStatus.noAccess');
  }

  getInstructorAccountStatusLabel(row: TableRow): string {
    if (row['credentialsActive'] === true) {
      return this.localization.translate('accountStatus.loggedIn');
    }
    if (row['invited'] === true) {
      return this.localization.translate('accountStatus.invited');
    }
    return this.localization.translate('accountStatus.notLoggedIn');
  }

  getCourseProgressView(row: TableRow): {
    name: string;
    completedHours: number;
    requiredHours: number;
    percent: number;
  } | null {
    const summaries = Array.isArray(row['courseSummaries']) ? (row['courseSummaries'] as Array<Record<string, unknown>>) : [];
    if (summaries.length === 0) {
      return null;
    }
    const summary = summaries.find((item) => this.isActiveCourseSummary(item)) ?? summaries.find((item) => !this.isCompletedCourseSummary(item)) ?? summaries[0];
    if (summary['courseType'] === 'AD_HOC') {
      return null;
    }
    const name = this.firstText(summary['courseInstanceName'], summary['courseName']);
    const completedHours = Number(this.asNumber(summary['completedPracticeHours']).toFixed(1));
    const requiredHours = Number(this.asNumber(summary['requiredPracticeHours']).toFixed(1));
    const percent = requiredHours > 0 ? Math.min(Math.round((completedHours / requiredHours) * 100), 100) : 0;
    return {
      name: name || '-',
      completedHours,
      requiredHours,
      percent
    };
  }

  getMonthlyInstructorLessonsView(row: TableRow): MonthlyInstructorLessonsView | null {
    const totalHours = this.firstFiniteNumber(row['monthlyLessonHours']);
    if (totalHours == null) {
      return null;
    }
    return {
      totalHours,
      breakdown: this.getMonthlyInstructorLessonsBreakdown(row),
      monthLabel: this.firstText(row['monthlyLessonHoursMonth'])
    };
  }

  getMonthlyInstructorLessonsTooltip(row: TableRow): string {
    const view = this.getMonthlyInstructorLessonsView(row);
    if (!view) {
      return this.localization.translate('common.noData');
    }
    const monthLabel = view.monthLabel || this.localization.translate('resourceList.currentMonth');
    const totalLabel = this.localization.translate('common.total');
    const hours = this.localization.translate('common.hoursShort');

    if (view.breakdown.length === 0) {
      return `${monthLabel}\n${this.localization.translate('common.noData')}`;
    }

    return [
      monthLabel,
      ...view.breakdown.map((item) => `${item.category}: ${item.hours} ${hours}`),
      `${totalLabel}: ${view.totalHours} ${hours}`
    ].join('\n');
  }

  isInstructorList(): boolean {
    return this.entity()?.key === 'instructors';
  }

  canInviteInstructor(row: TableRow): boolean {
    return this.canEditEntities() && this.entity()?.key === 'instructors' && row['invited'] !== true && row['credentialsActive'] !== true;
  }

  private translateFallbackLabel(fallback: string): string {
    const translated = this.localization.translate(fallback);
    return translated === fallback ? fallback : translated;
  }

  hasActionsColumn(entityKey?: EntityConfig['key']): boolean {
    const key = entityKey ?? this.entity()?.key;
    return (
      key === 'students' ||
      key === 'instructors' ||
      key === 'courses' ||
      key === 'courseInstances' ||
      key === 'payments' ||
      key === 'lessons' ||
      key === 'resources'
    );
  }

  canShowEditAction(row?: TableRow): boolean {
    const key = this.entity()?.key;
    if (key === 'courseInstances' && row && this.getEffectiveStatusForList(key, row) === 'finished') {
      return false;
    }
    return (
      this.canEditEntities() &&
      (key === 'students' ||
        key === 'instructors' ||
        key === 'courses' ||
        key === 'courseInstances' ||
        key === 'payments' ||
        key === 'lessons' ||
        key === 'resources')
    );
  }

  getStatusActions(row: TableRow): StatusAction[] {
    const key = this.entity()?.key;
    if (!key) {
      return [];
    }
    if ((key === 'students' || key === 'instructors' || key === 'courseInstances') && !this.canEditEntities()) {
      return [];
    }
    if (key === 'payments' && !this.canAddPayment()) {
      return [];
    }
    if (key === 'lessons' && !this.canAddLesson()) {
      return [];
    }
    const status = this.getEffectiveStatusForList(key, row);

    if (key === 'students') {
      if (status === 'active') {
        return [{ nextStatus: 'disabled', labelKey: 'common.disable', icon: 'block' }];
      }
      if (status === 'disabled') {
        return [{ nextStatus: 'active', labelKey: 'common.activate', icon: 'check_circle' }];
      }
      return [];
    }

    if (key === 'instructors') {
      if (status === 'active') {
        return [
          { nextStatus: 'disabled', labelKey: 'common.disable', icon: 'block' },
          { nextStatus: 'suspended', labelKey: 'common.suspend', icon: 'pause_circle' }
        ];
      }
      if (status === 'disabled' || status === 'suspended') {
        return [{ nextStatus: 'active', labelKey: 'common.activate', icon: 'check_circle' }];
      }
      return [];
    }

    if (key === 'courseInstances') {
      if (status === 'active') {
        return [{ nextStatus: 'finished', labelKey: 'common.finish', icon: 'task_alt' }];
      }
      return [];
    }

    if (key === 'payments') {
      if (status === 'pendingSettlement') {
        if (this.isInstructorCashPayment(row)) {
          return [{ nextStatus: 'settleCash', labelKey: 'payment.settleCash', icon: 'point_of_sale' }];
        }
        return [{ nextStatus: 'paid', labelKey: 'common.markPaid', icon: 'payments' }];
      }
      return [];
    }

    if (key === 'lessons') {
      if (status === 'planned') {
        return [{ nextStatus: 'cancelled', labelKey: 'common.cancelLesson', icon: 'event_busy' }];
      }
      return [];
    }

    return [];
  }

  private isInstructorCashPayment(row: TableRow | Record<string, unknown>): boolean {
    return `${row['collectedByInstructorId'] ?? ''}`.trim().length > 0;
  }

  getQuickActions(row: TableRow): RowQuickAction[] {
    const key = this.entity()?.key;
    if (!key) {
      return [];
    }

    if (key === 'students' && this.canAssignStudentRelations()) {
      return [
        { key: 'assignStudentCourse', labelKey: 'resourceList.enrollCourse', icon: 'assignment' },
        { key: 'assignStudentInstructor', labelKey: 'resourceDetails.assignInstructor', icon: 'person_add' }
      ];
    }

    if (key === 'instructors') {
      const actions: RowQuickAction[] = [];
      if (this.canInviteInstructor(row)) {
        actions.push({ key: 'inviteInstructor', labelKey: 'invitationSettings.manualInvite', icon: 'outgoing_mail' });
      }
      if (this.canAssignStudentRelations()) {
        actions.push(
          { key: 'assignInstructorCourse', labelKey: 'resourceDetails.assignCourse', icon: 'assignment' },
          { key: 'assignInstructorResource', labelKey: 'resourceDetails.assignResource', icon: 'directions_car' }
        );
      }
      return actions;
    }

    if (key === 'resources' && this.canEditEntities()) {
      return [{ key: 'assignResourceCourse', labelKey: 'resourceDetails.assignCourse', icon: 'event_available' }];
    }

    if (key === 'lessons' && this.canDeleteCompletedLesson(row)) {
      return [{ key: 'deleteLesson', labelKey: 'lessonCorrection.delete', icon: 'delete' }];
    }

    return [];
  }

  private canDeleteCompletedLesson(row: TableRow): boolean {
    const status = this.getEffectiveStatusForList('lessons', row);
    const settlementId = row['settlementId'] ?? row['instructorSettlementId'];
    return this.canAddLesson() && status === 'completed' && (settlementId === null || settlementId === undefined || settlementId === '');
  }

  async openQuickAction(row: TableRow, action: RowQuickAction, event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (action.key === 'inviteInstructor') {
      const dialogRef = this.openImmediateActionConfirmation(action.labelKey);
      dialogRef.afterClosed().subscribe((result) => {
        if (result?.confirmed) void this.dataService.inviteInstructor(row.id);
      });
      return;
    }

    if (action.key === 'deleteLesson') {
      const dialogRef = this.dialog.open(DeleteLessonDialogComponent, {
        width: '480px',
        maxWidth: 'calc(100vw - 24px)'
      });
      dialogRef.afterClosed().subscribe((payload) => {
        if (!payload) return;
        this.listLoading.set(true);
        void this.dataService.deleteLessonEnteredByMistake(row.id, payload.reason)
          .then(() => this.listRefresh.update((value) => value + 1))
          .finally(() => this.listLoading.set(false));
      });
      return;
    }

    if (action.key === 'assignStudentCourse') {
      this.openAssignDialog({
        titleKey: 'resourceList.enrollCourse',
        labelKey: 'resourceDetails.availableCourseInstances',
        emptyMessageKey: 'resourceDetails.noAvailableCourses',
        actionLabelKey: 'resourceList.enrollCourse',
        options: this.availableCoursesForStudent(row),
        onAssign: (courseInstanceId) => this.dataService.assignStudentToCourseInstance(row.id, courseInstanceId, true)
      });
      return;
    }

    if (action.key === 'assignStudentInstructor') {
      const options = await this.availableInstructorsForStudent(row);
      this.openAssignDialog({
        titleKey: 'resourceDetails.assignInstructor',
        labelKey: 'resourceDetails.availableInstructors',
        emptyMessageKey: 'resourceDetails.noAvailableInstructors',
        actionLabelKey: 'resourceDetails.assignInstructor',
        options,
        onAssign: (instructorId) => this.dataService.assignStudentToInstructor(row.id, instructorId, true)
      });
      return;
    }

    if (action.key === 'assignInstructorCourse') {
      this.openAssignDialog({
        titleKey: 'resourceDetails.assignCourse',
        labelKey: 'resourceDetails.availableCourseInstances',
        emptyMessageKey: 'resourceDetails.noAvailableCourses',
        actionLabelKey: 'resourceDetails.assignCourse',
        options: this.availableCoursesForInstructor(row),
        onAssign: (courseInstanceId) => this.dataService.assignInstructorToCourseInstance(row.id, courseInstanceId, true)
      });
      return;
    }

    if (action.key === 'assignInstructorResource') {
      const options = this.availableResourceAssignmentsForInstructor(row);
      this.openAssignDialog({
        titleKey: 'resourceDetails.assignResource',
        labelKey: 'entity.resources',
        emptyMessageKey: 'resourceDetails.noAvailableResourcesForInstructor',
        actionLabelKey: 'resourceDetails.assignResource',
        options: options.map((item) => ({ id: item.id, label: item.label })),
        onAssign: (selectedOptionId) => {
          const selected = options.find((item) => this.sameId(item.id, selectedOptionId));
          if (selected) {
            this.dataService.assignResourceToInstructor(row.id, selected.resourceId, true);
          }
        },
        width: '480px'
      });
      return;
    }

    if (action.key === 'assignResourceCourse') {
      this.openAssignDialog({
        titleKey: 'resourceDetails.assignCourse',
        labelKey: 'resourceDetails.availableCourseInstances',
        emptyMessageKey: 'resourceDetails.noAvailableCourses',
        actionLabelKey: 'resourceDetails.assignCourse',
        options: this.availableCoursesForResource(row),
        onAssign: (courseInstanceId) => this.dataService.assignResourceToCourseInstance(row.id, courseInstanceId, true)
      });
    }
  }

  readonly paymentSettlementsPending = signal<ReadonlySet<string>>(new Set());

  applyStatusAction(row: TableRow, action: StatusAction, event: Event): void {
    event.stopPropagation();
    const entityKey = this.entity()?.key;
    if (!entityKey) {
      return;
    }
    if (entityKey === 'payments' && action.nextStatus === 'settleCash') {
      const key = String(row.id);
      if (this.paymentSettlementsPending().has(key)) return;
      this.paymentSettlementsPending.update(ids => new Set([...ids, key]));
      const release = () => this.paymentSettlementsPending.update(ids => new Set([...ids].filter(id => id !== key)));
      const dialogRef = this.openImmediateActionConfirmation(action.labelKey);
      dialogRef.afterClosed().subscribe((result) => {
        if (!result?.confirmed) { release(); return; }
        this.listLoading.set(true);
        this.dataService.settlePaymentCash$(row.id).subscribe({
          next: payment => { Object.assign(row, payment); this.listRefresh.update((value) => value + 1); },
          error: () => { release(); this.listLoading.set(false); },
          complete: () => { release(); this.listLoading.set(false); }
        });
      });
      return;
    }
    if (entityKey === 'lessons' && action.nextStatus === 'cancelled') {
      const dialogRef = this.dialog.open(CancelLessonDialogComponent, {
        width: '520px',
        maxWidth: 'calc(100vw - 32px)'
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.executeTableStatusMutation('lessons', row.id, 'cancelled', result);
        }
      });
      return;
    }
    if (this.requiresStatusConfirmation(entityKey, action.nextStatus)) {
      const dialogRef = this.dialog.open(ConfirmStatusDialogComponent, {
        width: '420px',
        maxWidth: 'calc(100vw - 32px)',
        data: {
          actionLabel: this.localization.translate(action.labelKey).toLowerCase(),
          showDateCompleted: entityKey === 'courseInstances' && action.nextStatus === 'finished'
        } satisfies ConfirmStatusDialogData
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result?.confirmed) {
          this.executeTableStatusMutation(entityKey, row.id, action.nextStatus, this.buildStatusPayload(entityKey, result));
        }
      });
      return;
    }
    this.executeTableStatusMutation(entityKey, row.id, action.nextStatus);
  }

  private executeTableStatusMutation(
    entityKey: CollectionKey,
    id: string | number,
    status: string,
    payload: Record<string, unknown> = {}
  ): void {
    this.listLoading.set(true);
    this.dataService.setStatus$(entityKey, id, status, payload).subscribe({
      next: () => this.listRefresh.update((value) => value + 1),
      error: () => this.listLoading.set(false),
      complete: () => this.listLoading.set(false)
    });
  }

  private requiresStatusConfirmation(entityKey: EntityConfig['key'], nextStatus: string): boolean {
    return entityKey !== 'lessons' || nextStatus !== 'cancelled';
  }

  private openImmediateActionConfirmation(actionLabelKey: string): MatDialogRef<ConfirmStatusDialogComponent, ConfirmStatusDialogResult> {
    return this.dialog.open(ConfirmStatusDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      data: {
        actionLabel: this.localization.translate(actionLabelKey).toLowerCase(),
        showDateCompleted: false
      } satisfies ConfirmStatusDialogData
    });
  }

  private buildStatusPayload(entityKey: EntityConfig['key'], result: ConfirmStatusDialogResult): Record<string, unknown> {
    if (entityKey === 'courseInstances' && result.dateCompleted) {
      return { dateCompleted: result.dateCompleted };
    }
    return {};
  }

  async openEditEntity(row: TableRow, event: Event): Promise<void> {
    event.stopPropagation();
    const config = this.entity();
    if (!config || !this.canShowEditAction(row)) {
      return;
    }

    if (config.key === 'resources') {
      const dialogRef = this.dialog.open(EditResourceDialogComponent, {
        width: '980px',
        maxWidth: '96vw',
        data: {
          resource: {
            id: row.id,
            name: `${row['name'] ?? ''}`,
            registration: `${row['registration'] ?? ''}`,
            category: `${row['category'] ?? ''}`,
            type: `${row['type'] ?? ''}`,
            subType: `${row['subType'] ?? ''}`,
            nextServiceDate: (row['nextServiceDate'] as string | null | undefined) ?? null,
            issuanceExpiryDate: (row['issuanceExpiryDate'] as string | null | undefined) ?? null
          },
          categoryOptions: this.dataService.categories().map((item) => ({ value: item.name, label: item.name })),
          typeOptions: this.resourceTypeOptions(),
          subTypeOptions: this.resourceSubTypeOptions()
        }
      });

      dialogRef.afterClosed().subscribe((patch) => {
        if (!patch) {
          return;
        }
        void this.saveEntityEdit(config.key, row, patch as Record<string, unknown>);
      });
      return;
    }

    if (config.key === 'lessons') {
      try {
        await this.dataService.loadLessonEditReferences(row);
      } catch {
        // Loading errors are reported by the data service.
        return;
      }
    }

    const dialogRef = this.dialog.open(CreateResourceDialogComponent, {
      data: {
        entity: config,
        mode: 'edit',
        initialValues: this.buildEditInitialValues(config.key, row),
        selectOptions: this.buildSelectOptions(config.key),
        courseTypes: Object.fromEntries(
          this.dataService.courses().map((course) => [String(course.id), course.courseType ?? 'STANDARD'])
        ),
        statusOptions: config.statuses ?? []
      },
      width: config.key === 'instructors' ? '1180px' : '1120px',
      maxHeight: '95vh',
      maxWidth: '96vw'
    });

    dialogRef.afterClosed().subscribe((payload) => {
      if (!payload) {
        return;
      }
      void this.saveEntityEdit(config.key, row, payload);
    });
  }

  private openAssignDialog(config: {
      titleKey: string;
      labelKey: string;
      emptyMessageKey: string;
      actionLabelKey: string;
      options: AssignOption[];
      onAssign: (id: string | number) => void;
      width?: string;
  }): void {
    const dialogRef = this.dialog.open(AssignRelationDialogComponent, {
      width: config.width ?? '420px',
      data: {
        title: this.localization.translate(config.titleKey),
        label: this.localization.translate(config.labelKey),
        options: config.options,
        emptyMessage: this.localization.translate(config.emptyMessageKey),
        actionLabel: this.localization.translate(config.actionLabelKey)
      }
    });

    dialogRef.afterClosed().subscribe((id) => {
      if (!id) {
        return;
      }
      config.onAssign(id);
    });
  }

  private async availableInstructorsForStudent(row: TableRow): Promise<AssignOption[]> {
    const instructors = await this.dataService.fetchAllCollectionItems('instructors', {
      status: 'ACTIVE',
      availableForStudentId: row.id,
      sort: 'surname',
      order: 'asc'
    }, 50);
    return instructors
      .map((item) => ({ id: item.id, label: `${item.name} ${item.surname}` }));
  }

  private availableCoursesForStudent(row: TableRow): AssignOption[] {
    const assigned = new Set(this.asNumberArray(row['courseInstanceIds']));
    return this.dataService
      .courseInstances()
      .filter((item) => this.isActiveCourseInstance(item))
      .filter((item) => !this.hasId(assigned, item.id))
      .map((item) => ({ id: item.id, label: item.name }));
  }

  private instructorCourses(row: TableRow): Array<{ id: string | number; name: string; status?: unknown; category?: string; categories?: string[] }> {
    const assignedCourseIds = new Set(this.asNumberArray(row['courseInstanceIds']));
    return this.dataService.courseInstances().filter((item) => this.hasId(assignedCourseIds, item.id));
  }

  private availableCoursesForInstructor(row: TableRow): AssignOption[] {
    const assigned = new Set(this.asNumberArray(row['courseInstanceIds']));
    return this.dataService
      .courseInstances()
      .filter((item) => this.isActiveCourseInstance(item))
      .filter((item) => !this.hasId(assigned, item.id))
      .map((item) => ({ id: item.id, label: item.name }));
  }

  private availableResourceAssignmentsForInstructor(row: TableRow): InstructorResourceAssignmentOption[] {
    const assignedIds = this.asNumberArray(row['resourceIds']);
    return this.dataService.resources()
      .filter((resource) => !assignedIds.some((id) => this.sameId(id, resource.id)))
      .map((resource) => ({ id: resource.id, resourceId: resource.id, label: resource.name }));
  }

  private availableCoursesForResource(row: TableRow): AssignOption[] {
    const assigned = new Set(this.asNumberArray(row['courseInstanceIds']));
    const resourceCategories = new Set(this.asStringArray(row['categories'] ?? row['category']));
    return this.dataService
      .courseInstances()
      .filter((item) => this.isActiveCourseInstance(item))
      .filter((item) => !this.hasId(assigned, item.id))
      .filter((item) => {
        const courseCategories = this.asStringArray(item.categories ?? item.category);
        return courseCategories.length === 0 || courseCategories.some((category) => resourceCategories.has(category));
      })
      .map((item) => ({ id: item.id, label: `${item.name} (${this.asStringArray(item.categories ?? item.category).join(', ')})` }));
  }

  private isActiveCourseInstance(row: { status?: unknown }): boolean {
    return `${row.status ?? ''}` === 'active';
  }

  private resolveReferenceValue(key: string, value: unknown): string {
    const id = value;
    if (id == null || `${id}`.trim().length === 0) {
      return `${value ?? ''}`;
    }

    if (key === 'studentId' || key === 'studentIds') {
      const student = this.dataService.students().find((item) => this.sameId(item.id, id));
      return student ? `${student.name} ${student.surname}` : '';
    }
    if (key === 'instructorId' || key === 'instructorIds' || key === 'collectedByInstructorId') {
      const instructor = this.dataService.instructors().find((item) => this.sameId(item.id, id));
      return instructor ? `${instructor.name} ${instructor.surname}` : '';
    }
    if (key === 'licenceCategoryIds' || key === 'licenceCategories' || key === 'category' || key === 'categories') {
      return `${value ?? ''}`;
    }
    if (key === 'organisationId') {
      return this.dataService.organisations().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'courseInstanceId' || key === 'courseInstanceIds') {
      return this.dataService.courseInstances().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'courseId') {
      return this.dataService.courses().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'categoryId' || key === 'categoryIds') {
      return this.dataService.categories().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'resourceId') {
      return this.dataService.resources().find((item) => this.sameId(item.id, id))?.name ?? '';
    }
    if (key === 'addressId') {
      const address = this.dataService.addresses().find((item) => this.sameId(item.id, id));
      return address ? `${address.streetName} ${address.buildingNumber}, ${address.city}` : '';
    }
    if (key === 'contactId') {
      const contact = this.dataService.contacts().find((item) => this.sameId(item.id, id));
      return contact ? `${contact.email} | ${contact.mobile}` : '';
    }

    return `${value ?? ''}`;
  }

  private embeddedReferenceValue(row: TableRow, key: string): string {
    if (key === 'studentId') {
      return this.lessonReferenceText(row, 'student');
    }
    if (key === 'instructorId') {
      return this.lessonReferenceText(row, 'instructor');
    }
    if (key === 'collectedByInstructorId') {
      return this.firstText(
        row['collectedByInstructorName'],
        row['collectedByInstructorDisplayName'],
        row['collectedByDisplay'],
        row['paidTo']
      );
    }
    if (key === 'resourceId') {
      return this.lessonReferenceText(row, 'resource');
    }
    if (key === 'courseInstanceId') {
      return this.lessonReferenceText(row, 'courseInstance');
    }
    return '';
  }

  private lessonReferenceText(row: TableRow, prefix: 'student' | 'instructor' | 'resource' | 'courseInstance'): string {
    return this.firstText(
      row[`${prefix}Name`],
      row[`${prefix}DisplayName`],
      this.summaryText(row[prefix])
    );
  }

  private summaryText(value: unknown): string {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return '';
    }
    const row = value as Record<string, unknown>;
    return this.firstText(row['displayName'], row['name']);
  }

  private firstText(...values: unknown[]): string {
    for (const value of values) {
      const text = `${value ?? ''}`.trim();
      if (text) {
        return text;
      }
    }
    return '';
  }

  private isMoneyKey(key: string): boolean {
    return key === 'amount' || key === 'price';
  }

  getPaymentStatusLabel(row: TableRow): string {
    const status = this.getPaymentBalanceStatus(row);
    const keyMap: Record<string, string> = {
      exceeded: 'instructor.overview.healthExceeded',
      close: 'instructor.overview.healthClose',
      fine: 'instructor.overview.healthFine',
      unknown: 'common.noData'
    };
    return this.localization.translate(keyMap[status] ?? keyMap['unknown']);
  }

  getPaymentBalanceValueLabel(row: TableRow): string {
    const balance = this.getPaymentBalance(row);
    if (!balance) {
      return '-';
    }
    return this.formatMoney(this.getRideBalanceAmount(balance));
  }

  getPaymentStatusClass(row: TableRow): string {
    const status = this.getPaymentBalanceStatus(row);
    return `payment-status-pill payment-status-${status}`;
  }

  getPaymentStatusIcon(row: TableRow): string {
    const status = this.getPaymentBalanceStatus(row);
    const iconMap: Record<string, string> = {
      exceeded: 'error_outline',
      close: 'warning_amber',
      fine: 'check_circle',
      unknown: 'help_outline'
    };
    return iconMap[status] ?? iconMap['unknown'];
  }

  getPaymentBalanceDetails(row: TableRow): Array<{ label: string; value: string }> {
    const balance = this.getPaymentBalance(row);
    if (!balance) {
      return [{ label: this.localization.translate('common.status'), value: this.localization.translate('common.noData') }];
    }
    const totalDue = this.asNumber(balance['totalDue']);
    const totalPaid = this.asNumber(balance['totalPaid'] ?? balance['paidAmount']);
    const pending = this.asNumber(balance['pendingSettlementAmount']);
    const remainingToPay = this.asNumber(balance['remainingAmount'] ?? Math.max(-this.asNumber(balance['balance']), 0));
    const completedPracticeHours = this.asNumber(balance['completedPracticeHours']);
    const requiredPracticeHours = this.asNumber(balance['totalRequiredPracticeHours']);
    const averageHourlyRate = this.asNumber(balance['averageHourlyRate']);
    const used = this.asNumber(balance['usedFunds'] ?? completedPracticeHours * averageHourlyRate);
    const rideBalance = this.getRideBalanceAmount(balance);
    const details = [
      { label: this.localization.translate('common.balance'), value: this.formatMoney(rideBalance) },
      { label: this.localization.translate('paymentBalance.totalDue'), value: this.formatMoney(totalDue) },
      { label: this.localization.translate('paymentBalance.totalPaid'), value: this.formatMoney(totalPaid) },
      { label: this.localization.translate('paymentBalance.remainingToPay'), value: this.formatMoney(remainingToPay) },
      {
        label: this.localization.translate('paymentBalance.completedPracticeHours'),
        value: `${this.formatHours(completedPracticeHours)} / ${this.formatHours(requiredPracticeHours)}`
      },
      { label: this.localization.translate('paymentBalance.usedByProgress'), value: this.formatMoney(used) }
    ];

    if (pending > 0) {
      details.splice(2, 0, {
        label: this.localization.translate('paymentBalance.pendingSettlementAmount'),
        value: this.formatMoney(pending)
      });
    }

    return details;
  }

  getPaymentBalanceAriaLabel(row: TableRow): string {
    const details = this.getPaymentBalanceDetails(row)
      .map((item) => `${item.label}: ${item.value}`)
      .join(', ');
    return `${this.getPaymentStatusLabel(row)}. ${details}`;
  }

  getPaymentBalanceTooltip(row: TableRow): string {
    const details = this.getPaymentBalanceDetails(row)
      .map((item) => `${item.label}: ${item.value}`)
      .join('\n');
    return `${this.getPaymentStatusLabel(row)}\n${details}`;
  }

  private getPaymentBalanceStatus(row: TableRow): 'fine' | 'close' | 'exceeded' | 'unknown' {
    const balance = this.getPaymentBalance(row);
    if (!balance) {
      return 'unknown';
    }
    const totalPaid = this.asNumber(balance['totalPaid'] ?? balance['paidAmount']);
    const totalDue = this.asNumber(balance['totalDue']);
    const remainingToPay = this.asNumber(balance['remainingAmount'] ?? Math.max(-this.asNumber(balance['balance']), 0));
    const completedPracticeHours = this.asNumber(balance['completedPracticeHours']);
    const averageHourlyRate = this.asNumber(balance['averageHourlyRate']);
    const twoHourThreshold = this.asNumber(balance['twoHourBufferValue'] ?? (averageHourlyRate > 0 ? averageHourlyRate * 2 : 0));
    const rideBalance = this.getRideBalanceAmount(balance);
    if (rideBalance < 0) {
      return 'exceeded';
    }
    if (totalPaid === 0 && remainingToPay === 0 && totalDue === 0) {
      return 'fine';
    }
    if (remainingToPay > 0 && rideBalance === 0) {
      return 'close';
    }
    if (twoHourThreshold > 0 && rideBalance <= twoHourThreshold) {
      return 'close';
    }
    return 'fine';
  }

  private getRideBalanceAmount(balance: Record<string, unknown>): number {
    const totalPaid = this.asNumber(balance['totalPaid'] ?? balance['paidAmount']);
    const completedPracticeHours = this.asNumber(balance['completedPracticeHours']);
    const averageHourlyRate = this.asNumber(balance['averageHourlyRate']);
    const used = this.asNumber(balance['usedFunds'] ?? completedPracticeHours * averageHourlyRate);
    return this.asNumber(balance['rideBalance'] ?? totalPaid - used);
  }

  private getPaymentBalance(row: TableRow): Record<string, unknown> | null {
    const value = row['paymentBalance'];
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  }

  private asNumber(value: unknown): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private firstFiniteNumber(...values: unknown[]): number | null {
    for (const value of values) {
      if (value == null || `${value}`.trim() === '') {
        continue;
      }
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return null;
  }

  private formatHours(value: unknown): string {
    return `${Number(this.asNumber(value).toFixed(1))} ${this.localization.translate('common.hoursShort')}`;
  }

  private monthYearLabel(value: unknown): string {
    const match = `${value ?? ''}`.trim().match(/^(\d{4})-(\d{2})$/);
    return match ? `${match[2]}-${match[1]}` : '';
  }

  private translateStatus(status: string): string {
    const translated = this.localization.translate(`status.${status}`);
    if (translated !== `status.${status}`) {
      return translated;
    }
    const commonTranslated = this.localization.translate(`common.${status}`);
    return commonTranslated === `common.${status}` ? status : commonTranslated;
  }

  private getEffectiveStatusForList(entityKey: EntityConfig['key'], row: TableRow): string {
    return row.status;
  }

  private formatMoney(value: unknown): string {
    return formatPln(value);
  }

  private isDateKey(key: string): boolean {
    return ['birthDate', 'executionDate', 'paymentDate', 'dateStarted', 'dateCompleted', 'nextServiceDate', 'issuanceExpiryDate', 'cashSettledAt'].includes(key);
  }

  private formatCancellationSummary(row: Record<string, unknown>): string {
    const status = `${row['status'] ?? ''}`;
    if (status !== 'cancelled') {
      return '';
    }
    const parts = [
      row['cancellationConsequence'] === 'WITH_CONSEQUENCES'
        ? this.localization.translate('lessonCancel.badgeWithConsequences')
        : row['cancellationConsequence'] === 'WITHOUT_CONSEQUENCES'
          ? this.localization.translate('lessonCancel.badgeWithoutConsequences')
          : this.translateStatus(status)
    ];
    if (row['cancellationReasonType'] === 'EMERGENCY') {
      parts.push(this.localization.translate('lessonCancel.emergencyBadge'));
    }
    const charge = this.asNumber(row['studentCancellationChargeAmount']);
    if (charge > 0) {
      parts.push(this.formatMoney(charge));
    }
    const reason = `${row['cancellationReason'] ?? ''}`.trim();
    if (reason) {
      parts.push(reason);
    }
    return parts.join(' | ');
  }

  private formatDate(value: unknown): string {
    if (`${value ?? ''}` === '1900-01-01') return this.localization.translate('student.identity.legacyMissing');
    return formatDisplayDate(value);
  }

  private withEntityDefaults(entityKey: EntityConfig['key'], payload: Record<string, unknown>): Record<string, unknown> {
    if (entityKey === 'students') {
      const pesel = `${payload['personalIdentificationNumber'] ?? ''}`.trim();
      const birthDate = `${payload['birthDate'] ?? ''}`.trim();
      return {
        name: payload['name'],
        surname: payload['surname'],
        ...(pesel ? { personalIdentificationNumber: pesel } : {}),
        ...(birthDate ? { birthDate } : {}),
        additionalInformation: '',
        email: payload['email'],
        mobile: payload['mobile'],
        streetName: payload['streetName'],
        buildingNumber: payload['buildingNumber'],
        postalCode: payload['postalCode'],
        city: payload['city'],
        country: payload['country'],
        status: 'active',
        instructorIds: [],
        courseInstanceIds: []
      };
    }

    if (entityKey === 'instructors') {
      const licenceCategories = this.asStringArray(payload['licenceCategories'] ?? payload['licenceCategoryIds']);
      return {
        name: payload['name'],
        surname: payload['surname'],
        licenceNumber: payload['licenceNumber'],
        licenceCategories,
        hourlyRateByCategory: this.buildInstructorHourlyRates(
          licenceCategories,
          payload['hourlyRateByCategory']
        ),
        email: payload['email'],
        mobile: payload['mobile'],
        streetName: payload['streetName'],
        buildingNumber: payload['buildingNumber'],
        postalCode: payload['postalCode'],
        city: payload['city'],
        country: payload['country'],
        status: 'active',
        studentIds: [],
        courseInstanceIds: []
      };
    }

    if (entityKey === 'resources') {
      const categories = this.asStringArray(payload['categories'] ?? payload['category'] ?? payload['categoryId']);
      const category = categories[0] ?? '';
      return {
        ...payload,
        category,
        categories,
        type: normalizeResourceType(payload['type']),
        subType: normalizeResourceSubType(payload['subType']),
        nextServiceDate: `${payload['nextServiceDate'] ?? ''}` || null,
        issuanceExpiryDate: `${payload['issuanceExpiryDate'] ?? ''}` || null,
        status: 'available',
        courseInstanceIds: []
      };
    }

    if (entityKey === 'courseInstances') {
      const product = this.dataService.courses().find((item) => this.sameId(item.id, payload['courseId']));
      const adHoc = product?.courseType === 'AD_HOC';
      return {
        ...payload,
        courseId: payload['courseId'] ?? this.dataService.courses()[0]?.id ?? '',
        courseType: product?.courseType ?? 'STANDARD',
        billingModel: adHoc ? 'HOURLY' : 'FIXED_PRICE',
        price: adHoc ? 0 : Number(payload['price'] ?? 0),
        studentHourlyPrice: adHoc ? Number(payload['studentHourlyPrice']) : null,
        dateStarted: `${payload['dateStarted'] ?? new Date().toISOString().slice(0, 10)}`,
        dateCompleted: null,
        status: 'active',
        additionalInformation: '',
        studentIds: [],
        instructorIds: []
      };
    }

    if (entityKey === 'contacts') {
      return { ...payload, mobile: payload['mobile'] ?? '', status: 'active' };
    }

    if (entityKey === 'addresses') {
      return {
        ...payload,
        buildingNumber: '',
        postalCode: '',
        status: 'active'
      };
    }

    if (entityKey === 'organisations') {
      return { ...payload, addressId: 1, contactId: 1, status: 'active' };
    }

    if (entityKey === 'courses') {
      const categories = this.asStringArray(payload['categories'] ?? payload['categoryId']);
      const courseType = payload['courseType'] === 'AD_HOC' ? 'AD_HOC' : 'STANDARD';
      return {
        ...payload,
        courseType,
        categories: courseType === 'AD_HOC' ? categories.slice(0, 1) : categories,
        theoryHours: courseType === 'AD_HOC' ? null : this.asNonNegativeNumber(payload['theoryHours'] ?? payload['theoreticalLessonsNumber']),
        practiceHours: courseType === 'AD_HOC' ? null : this.asNonNegativeNumber(payload['practiceHours'] ?? payload['practicalLessonsNumber']),
        status: 'active'
      };
    }

    if (entityKey === 'categories') {
      return { ...payload, status: 'active' };
    }

    if (entityKey === 'payments') {
      return {
        ...payload,
        studentId: payload['studentId'] ?? '',
        courseInstanceId: payload['courseInstanceId'] ?? '',
        amount: Number(payload['amount'] ?? 0),
        paymentDate: `${payload['paymentDate'] ?? new Date().toISOString().slice(0, 10)}`,
        paidTo: `${payload['paidTo'] ?? 'OS Portal Academy'}`,
        paymentType: `${payload['paymentType'] ?? 'card'}`,
        status: 'paid',
        additionalInformation: `${payload['additionalInformation'] ?? ''}`
      };
    }

    if (entityKey === 'lessons') {
      return {
        ...payload,
        resourceId: payload['resourceId'] ?? '',
        courseInstanceId: payload['courseInstanceId'] ?? '',
        lessonTime: payload['lessonTime'] ?? '08:00',
        durationMinutes: Number(payload['durationMinutes'] ?? 90),
        status: 'planned',
        studentComment: `${payload['studentComment'] ?? ''}`,
        instructorPrivateNote: `${payload['instructorPrivateNote'] ?? ''}`,
        additionalInformation: `${payload['additionalInformation'] ?? ''}`
      };
    }

    return payload;
  }

  private buildEditInitialValues(entityKey: EntityConfig['key'], row: TableRow): Record<string, unknown> {
    if (entityKey === 'students') {
      const contact = this.dataService.contacts().find((item) => this.sameId(item.id, row['contactId']));
      const address =
        this.dataService.addresses().find((item) => this.sameId(item.id, row['addressId'])) ??
        this.asEmbeddedAddress(row['address']);
      return {
        name: row['name'],
        surname: row['surname'],
        personalIdentificationNumber: '',
        personalIdentificationNumberMasked: row['personalIdentificationNumberMasked'],
        birthDate: row['birthDate'],
        email: contact?.email ?? '',
        mobile: contact?.mobile ?? '',
        streetName: address?.streetName ?? '',
        buildingNumber: address?.buildingNumber ?? '',
        postalCode: address?.postalCode ?? '',
        city: address?.city ?? '',
        country: address?.country ?? '',
        status: row['status']
      };
    }

    if (entityKey === 'instructors') {
      const contact = this.dataService.contacts().find((item) => this.sameId(item.id, row['contactId']));
      const address =
        this.dataService.addresses().find((item) => this.sameId(item.id, row['addressId'])) ??
        this.asEmbeddedAddress(row['address']);
      return {
        name: row['name'],
        surname: row['surname'],
        licenceNumber: row['licenceNumber'],
        licenceCategories: this.resolveLicenceCategoriesForEdit(row),
        hourlyRateByCategory: row['hourlyRateByCategory'],
        email: contact?.email ?? '',
        mobile: contact?.mobile ?? '',
        streetName: address?.streetName ?? '',
        buildingNumber: address?.buildingNumber ?? '',
        postalCode: address?.postalCode ?? '',
        city: address?.city ?? '',
        country: address?.country ?? '',
        status: row['status']
      };
    }

    if (entityKey === 'courseInstances') {
      return {
        ...row,
        theoreticalLessonsNumber: this.asNonNegativeNumber(row['theoreticalLessonsNumber']),
        practicalLessonsNumber: this.asNonNegativeNumber(row['practicalLessonsNumber'], row['lessonsNumber'])
      };
    }

    if (entityKey === 'courses') {
      return {
        ...row,
        theoreticalLessonsNumber: this.asNonNegativeNumber(row['theoreticalLessonsNumber']),
        practicalLessonsNumber: this.asNonNegativeNumber(row['practicalLessonsNumber'])
      };
    }

    if (entityKey === 'lessons') {
      return {
        ...row,
        durationHours: Number((Number(row['durationMinutes'] ?? 0) / 60).toFixed(1))
      };
    }

    return { ...row };
  }

  private async saveEntityEdit(entityKey: EntityConfig['key'], row: TableRow, payload: Record<string, unknown>): Promise<void> {
    this.listLoading.set(true);
    try {
      await this.applyEntityEdit(entityKey, row, payload);
      this.listRefresh.update((value) => value + 1);
    } catch {
      // The data service displays the mutation error.
    } finally {
      this.listLoading.set(false);
    }
  }

  private async applyEntityEdit(entityKey: EntityConfig['key'], row: TableRow, payload: Record<string, unknown>): Promise<void> {
    if (entityKey === 'students') {
      const studentPatch: Record<string, unknown> = {
        name: `${payload['name'] ?? ''}`,
        surname: `${payload['surname'] ?? ''}`,
        status: `${payload['status'] ?? row['status']}`
      };
      const birthDate = `${payload['birthDate'] ?? ''}`.trim();
      if (birthDate) studentPatch['birthDate'] = birthDate;
      if (Object.prototype.hasOwnProperty.call(payload, 'personalIdentificationNumber')) {
        const replacementPesel = payload['personalIdentificationNumber'];
        studentPatch['personalIdentificationNumber'] = replacementPesel == null ? null : `${replacementPesel}`.trim();
      }
      const updates: Array<Promise<void>> = [this.dataService.updateDynamic('students', row.id, studentPatch)];

      const contactId = row['contactId'];
      if (contactId) {
        updates.push(this.dataService.updateDynamic('contacts', contactId, {
          email: `${payload['email'] ?? ''}`,
          mobile: `${payload['mobile'] ?? ''}`
        }));
      }

      const addressId = row['addressId'];
      if (addressId) {
        updates.push(this.dataService.updateDynamic('addresses', addressId, {
          streetName: `${payload['streetName'] ?? ''}`,
          buildingNumber: `${payload['buildingNumber'] ?? ''}`,
          postalCode: `${payload['postalCode'] ?? ''}`,
          city: `${payload['city'] ?? ''}`,
          country: `${payload['country'] ?? ''}`
        }));
      }
      await Promise.all(updates);
      return;
    }

    if (entityKey === 'instructors') {
      const licenceCategories = this.asStringArray(payload['licenceCategories'] ?? payload['licenceCategoryIds']);
      const updates: Array<Promise<void>> = [this.dataService.updateDynamic('instructors', row.id, {
        name: `${payload['name'] ?? ''}`,
        surname: `${payload['surname'] ?? ''}`,
        licenceNumber: `${payload['licenceNumber'] ?? ''}`,
        licenceCategories,
        hourlyRateByCategory: this.buildInstructorHourlyRates(licenceCategories, payload['hourlyRateByCategory']),
        status: `${payload['status'] ?? row['status']}`
      })];

      const contactId = row['contactId'];
      if (contactId) {
        updates.push(this.dataService.updateDynamic('contacts', contactId, {
          email: `${payload['email'] ?? ''}`,
          mobile: `${payload['mobile'] ?? ''}`
        }));
      }

      const addressId = row['addressId'];
      if (addressId) {
        updates.push(this.dataService.updateDynamic('addresses', addressId, {
          streetName: `${payload['streetName'] ?? ''}`,
          buildingNumber: `${payload['buildingNumber'] ?? ''}`,
          postalCode: `${payload['postalCode'] ?? ''}`,
          city: `${payload['city'] ?? ''}`,
          country: `${payload['country'] ?? ''}`
        }));
      }
      await Promise.all(updates);
      return;
    }

    if (entityKey === 'courseInstances') {
      await this.dataService.updateDynamic('courseInstances', row.id, {
        name: `${payload['name'] ?? ''}`,
        courseId: payload['courseId'] ?? row['courseId'] ?? '',
        price: Number(payload['price'] ?? row['price'] ?? 0),
        billingModel: payload['billingModel'] ?? row['billingModel'] ?? 'FIXED_PRICE',
        studentHourlyPrice: payload['studentHourlyPrice'] ?? null,
        status: `${payload['status'] ?? row['status']}`
      });
      return;
    }

    if (entityKey === 'courses') {
      await this.dataService.updateDynamic('courses', row.id, {
        name: `${payload['name'] ?? ''}`,
        categories: this.asStringArray(payload['categories'] ?? row['categories']),
        theoryHours: this.asNonNegativeNumber(payload['theoryHours'], row['theoryHours']),
        practiceHours: this.asNonNegativeNumber(payload['practiceHours'], row['practiceHours']),
        status: `${payload['status'] ?? row['status']}`
      });
      return;
    }

    if (entityKey === 'payments') {
      await this.dataService.updateDynamic('payments', row.id, {
        studentId: payload['studentId'] ?? row['studentId'] ?? '',
        courseInstanceId: payload['courseInstanceId'] ?? row['courseInstanceId'] ?? '',
        amount: Number(payload['amount'] ?? row['amount'] ?? 0),
        paymentDate: `${payload['paymentDate'] ?? row['paymentDate'] ?? ''}`,
        paidTo: `${payload['paidTo'] ?? row['paidTo'] ?? ''}`,
        paymentType: `${payload['paymentType'] ?? row['paymentType'] ?? ''}`,
        additionalInformation: `${payload['additionalInformation'] ?? row['additionalInformation'] ?? ''}`,
        status: `${payload['status'] ?? row['status']}`
      });
      return;
    }

    if (entityKey === 'lessons') {
      await this.dataService.updateDynamic('lessons', row.id, {
        studentId: payload['studentId'] ?? row['studentId'] ?? '',
        instructorId: payload['instructorId'] ?? row['instructorId'] ?? '',
        courseInstanceId: payload['courseInstanceId'] ?? row['courseInstanceId'] ?? '',
        resourceId: payload['resourceId'] ?? row['resourceId'] ?? '',
        executionDate: `${payload['executionDate'] ?? row['executionDate'] ?? ''}`,
        lessonTime: `${payload['lessonTime'] ?? row['lessonTime'] ?? '08:00'}`,
        durationMinutes: Number(payload['durationHours'] ?? Number(row['durationMinutes'] ?? 60) / 60) * 60,
        status: `${payload['status'] ?? row['status']}`
      });
      return;
    }

    if (entityKey === 'resources') {
      const categories = this.asStringArray(payload['categories'] ?? payload['category'] ?? row['categories'] ?? row['category']);
      const category = categories[0] ?? '';
      await this.dataService.updateDynamic('resources', row.id, {
        name: `${payload['name'] ?? ''}`,
        registration: `${payload['registration'] ?? ''}`,
        category,
        categories,
        type: normalizeResourceType(payload['type'] ?? row['type']),
        subType: normalizeResourceSubType(payload['subType'] ?? row['subType']),
        nextServiceDate: `${payload['nextServiceDate'] ?? ''}` || null,
        issuanceExpiryDate: `${payload['issuanceExpiryDate'] ?? ''}` || null
      });
    }
  }

  private buildSelectOptions(
    entityKey: EntityConfig['key']
  ): Record<string, Array<{ value: string | number; label: string }>> {
    if (entityKey === 'courses') {
      return {
        categories: this.dataService.categories().map((item) => ({ value: item.name, label: item.name }))
      };
    }

    if (entityKey === 'instructors') {
      return {
        licenceCategories: this.dataService.categories().map((item) => ({ value: item.name, label: item.name }))
      };
    }

    if (entityKey === 'courseInstances') {
      return {
        courseId: this.dataService.courses().map((item) => ({ value: item.id, label: `${item.name} (${(item.categories ?? []).join(', ')})` }))
      };
    }

    if (entityKey === 'payments') {
      return {
        studentId: this.dataService.students().map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` })),
        courseInstanceId: this.dataService.courseInstances().map((item) => ({ value: item.id, label: item.name })),
        paymentType: [
          { value: 'cash', label: this.localization.translate('common.paymentType.cash') },
          { value: 'card', label: this.localization.translate('common.paymentType.card') },
          { value: 'transfer', label: this.localization.translate('common.paymentType.transfer') }
        ]
      };
    }

    if (entityKey === 'lessons') {
      return {
        studentId: this.dataService.students().map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` })),
        instructorId: this.dataService.instructors().map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` })),
        courseInstanceId: this.dataService.courseInstances().map((item) => ({ value: item.id, label: item.name })),
        resourceId: this.dataService.resources().map((item) => ({
          value: item.id,
          label: item.registration ? `${item.name} (${item.registration})` : item.name
        }))
      };
    }

    if (entityKey === 'resources') {
      return {
        categories: this.dataService.categories().map((item) => ({ value: item.name, label: item.name })),
        type: this.resourceTypeOptions(),
        subType: this.resourceSubTypeOptions()
      };
    }

    if (entityKey === 'categories') {
      return {
        name: CATEGORY_DICTIONARY.map((item) => ({ value: item, label: item }))
      };
    }

    return {};
  }

  private asEmbeddedAddress(value: unknown):
    | {
        streetName?: string;
        buildingNumber?: string;
        postalCode?: string;
        city?: string;
        country?: string;
      }
    | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    const row = value as Record<string, unknown>;
    return {
      streetName: typeof row['streetName'] === 'string' ? row['streetName'] : undefined,
      buildingNumber: typeof row['buildingNumber'] === 'string' ? row['buildingNumber'] : undefined,
      postalCode: typeof row['postalCode'] === 'string' ? row['postalCode'] : undefined,
      city: typeof row['city'] === 'string' ? row['city'] : undefined,
      country: typeof row['country'] === 'string' ? row['country'] : undefined
    };
  }

  private formatEmbeddedAddress(value: unknown): string {
    const address = this.asEmbeddedAddress(value);
    if (!address) {
      return '';
    }
    const line1 = [address.streetName, address.buildingNumber].filter(Boolean).join(' ').trim();
    const line2 = [address.postalCode, address.city].filter(Boolean).join(' ').trim();
    const line3 = this.formatCountry(address.country);
    return [line1, line2, line3].filter((item) => item.length > 0).join(', ');
  }

  private formatCountry(value: unknown): string {
    const code = `${value ?? ''}`.trim();
    if (code === 'PL' || code.toLowerCase() === 'polska' || code.toLowerCase() === 'poland') {
      return this.localization.translate('country.PL');
    }
    return code;
  }

  private translateResourceType(value: unknown): string {
    const normalized = normalizeResourceType(value);
    return normalized ? this.localization.translate(`resource.type.${normalized}`) : `${value ?? ''}`;
  }

  private translateResourceSubType(value: unknown): string {
    const normalized = normalizeResourceSubType(value);
    return normalized ? this.localization.translate(`resource.subType.${normalized}`) : `${value ?? ''}`;
  }

  private resourceTypeOptions(): Array<{ value: string; label: string }> {
    return RESOURCE_TYPE_OPTIONS.map((item) => ({ value: item.value, label: this.localization.translate(item.labelKey) }));
  }

  private resourceSubTypeOptions(): Array<{ value: string; label: string }> {
    return RESOURCE_SUB_TYPE_OPTIONS.map((item) => ({ value: item.value, label: this.localization.translate(item.labelKey) }));
  }

  private buildInstructorHourlyRates(
    rawCategoryIds: unknown,
    rawExplicitRates?: unknown
  ): Record<string, number> {
    const categoryNames = this.asStringArray(rawCategoryIds);
    const explicitRates =
      rawExplicitRates && typeof rawExplicitRates === 'object'
        ? (rawExplicitRates as Record<string, unknown>)
        : undefined;
    const rates: Record<string, number> = {};
    categoryNames.forEach((categoryName) => {
        const explicitRate = explicitRates?.[categoryName];
        const parsedExplicit = Number(explicitRate);
        rates[categoryName] = Number.isFinite(parsedExplicit)
          ? parsedExplicit
          : resolveDefaultRateForCategoryName(categoryName);
    });
    return rates;
  }

  private asStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      const single = `${value ?? ''}`.trim();
      return single ? [single] : [];
    }
    return value.map((item) => `${item ?? ''}`.trim()).filter((item) => item.length > 0);
  }

  private resolveLicenceCategoriesForEdit(row: TableRow): string[] {
    const direct = this.asStringArray(row['licenceCategories']);
    if (direct.length > 0) {
      return direct;
    }
    return this.asStringArray(row['licenceCategoryIds'])
      .map((id) => this.dataService.categories().find((item) => this.sameId(item.id, id))?.name ?? id)
      .filter((item) => item.trim().length > 0);
  }

  private asNumberArray(value: unknown): Array<string | number> {
    if (!Array.isArray(value)) {
      const single = `${value ?? ''}`.trim();
      return single ? [value as string | number] : [];
    }
    return value.filter((item) => `${item ?? ''}`.trim().length > 0) as Array<string | number>;
  }

  private asNonNegativeNumber(value: unknown, fallback: unknown = 0): number {
    const parsed = Number(value ?? fallback);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.max(0, parsed);
  }

  private resolveTotalLessons(explicitTotal: unknown, theoreticalLessonsNumber: number, practicalLessonsNumber: number): number {
    const splitTotal = theoreticalLessonsNumber + practicalLessonsNumber;
    if (splitTotal > 0) {
      return splitTotal;
    }
    return this.asNonNegativeNumber(explicitTotal);
  }

  private buildDefaultFilter(entityKey?: EntityConfig['key'], statusOptions: string[] = []): FilterDialogData {
    const lessonsOptions = entityKey === 'lessons' ? this.getLessonFilterOptions() : this.getEmptyLessonFilterOptions();
    return {
      status: this.defaultStatusFilter(entityKey),
      apiFilters: {},
      columnFilters: {},
      dateFrom: '',
      dateTo: '',
      studentId: '',
      instructorId: '',
      categoryId: '',
      entityKey: entityKey ?? '',
      title: '',
      filterFields: [],
      apiFilterFields: this.apiFilterFieldsForEntity(entityKey),
      statusOptions,
      ...lessonsOptions
    };
  }

  private defaultStatusFilter(entityKey?: EntityConfig['key']): string {
    return entityKey === 'students' || entityKey === 'instructors' ? 'active' : '';
  }

  private filterTitleForEntity(entity: EntityConfig): string {
    return this.localization.translate('resourceList.filter.titleForEntity', {
      entity: this.lowerFirst(this.entityTitleForFilter(entity))
    });
  }

  private entityTitleForFilter(entity: EntityConfig): string {
    const translated = this.localization.translate(`entity.${entity.key}`);
    return translated === `entity.${entity.key}` ? entity.title : translated;
  }

  private filterFieldsForCurrentEntity(entity: EntityConfig | null = this.entity()): FilterFieldConfig[] {
    if (!entity) {
      return [];
    }
    return entity.columns
      .filter(
        (column) =>
          column.key !== 'status' &&
          column.key !== 'actions' &&
          column.key !== 'courseProgress' &&
          column.key !== 'monthlyLessonHours' &&
          !this.apiBackedColumnKeys(entity.key).has(column.key)
      )
      .map((column) => ({
        value: column.key,
        label: this.getFilterFieldLabel(entity, column.key, column.label),
        type: this.getFilterFieldType(entity, column.key),
        options: this.getFilterFieldOptions(entity, column.key)
      }));
  }

  private getFilterFieldLabel(entity: EntityConfig, columnKey: string, fallback: string): string {
    if (entity.key === 'courseInstances' && columnKey === 'name') {
      return this.localization.translate('field.courseName');
    }
    return this.getColumnLabel(columnKey, fallback);
  }

  private getFilterFieldType(entity: EntityConfig, columnKey: string): FilterFieldConfig['type'] {
    if (this.isDateKey(columnKey)) {
      return 'date';
    }
    if (columnKey === 'price' || columnKey === 'amount' || columnKey === 'theoryHours' || columnKey === 'practiceHours') {
      return 'number';
    }
    if (entity.key === 'courseInstances' && columnKey === 'courseId') {
      return 'select';
    }
    return 'text';
  }

  private getFilterFieldOptions(entity: EntityConfig, columnKey: string): FilterSelectOption[] | undefined {
    if (entity.key === 'courseInstances' && columnKey === 'courseId') {
      return this.dataService.courses().map((item) => ({ value: item.id, label: item.name }));
    }
    return undefined;
  }

  private apiBackedColumnKeys(entityKey: EntityConfig['key']): Set<string> {
    const keysByEntity: Partial<Record<EntityConfig['key'], string[]>> = {
      students: ['paymentStatus', 'courseProgress', 'personalIdentificationNumberMasked'],
      payments: ['studentId', 'courseInstanceId', 'amount', 'paymentDate', 'paymentType'],
      lessons: ['studentId', 'instructorId', 'resourceId', 'courseInstanceId', 'executionDate', 'category', 'lessonTime', 'durationMinutes'],
      courseInstances: ['courseId', 'category', 'studentId', 'instructorId', 'resourceId'],
      resources: ['categories', 'courseInstanceId', 'instructorId']
    };
    return new Set(keysByEntity[entityKey] ?? []);
  }

  private apiFilterFieldsForEntity(entityKey?: EntityConfig['key']): FilterFieldConfig[] {
    const query = this.apiFilterField('query', 'text');
    const externalReference = this.apiFilterField('externalReference', 'text');
    switch (entityKey) {
      case 'students':
        return [
          query,
          externalReference,
          this.apiFilterField('personalIdentificationNumber', 'text'),
          this.apiFilterField('paymentStatus', 'select', this.paymentStatusFilterOptions()),
          this.apiFilterField('courseProgress', 'select', this.courseProgressFilterOptions()),
          this.apiFilterField('instructorId', 'select', this.instructorFilterOptions()),
          this.apiFilterField('courseInstanceId', 'select', this.courseInstanceFilterOptions()),
          this.apiFilterField('availableForInstructorId', 'select', this.instructorFilterOptions()),
          this.apiFilterField('availableForCourseInstanceId', 'select', this.courseInstanceFilterOptions())
        ];
      case 'instructors':
        return [
          query,
          externalReference,
          this.apiFilterField('studentId', 'select', this.studentFilterOptions()),
          this.apiFilterField('courseInstanceId', 'select', this.courseInstanceFilterOptions()),
          this.apiFilterField('availableForStudentId', 'select', this.studentFilterOptions()),
          this.apiFilterField('availableForCourseInstanceId', 'select', this.courseInstanceFilterOptions())
        ];
      case 'courseInstances':
        return [
          query,
          externalReference,
          this.apiFilterField('courseId', 'select', this.courseFilterOptions()),
          this.apiFilterField('category', 'select', this.categoryFilterOptions()),
          this.apiFilterField('studentId', 'select', this.studentFilterOptions()),
          this.apiFilterField('instructorId', 'select', this.instructorFilterOptions()),
          this.apiFilterField('resourceId', 'select', this.resourceFilterOptions()),
          this.apiFilterField('availableForStudentId', 'select', this.studentFilterOptions()),
          this.apiFilterField('availableForInstructorId', 'select', this.instructorFilterOptions()),
          this.apiFilterField('availableForResourceId', 'select', this.resourceFilterOptions())
        ];
      case 'lessons':
        return [
          externalReference,
          this.apiFilterField('dateFrom', 'date'),
          this.apiFilterField('dateTo', 'date'),
          this.apiFilterField('studentQuery', 'text'),
          this.apiFilterField('studentId', 'select', this.studentFilterOptions()),
          this.apiFilterField('instructorId', 'select', this.instructorFilterOptions()),
          this.apiFilterField('resourceId', 'select', this.resourceFilterOptions()),
          this.apiFilterField('courseInstanceId', 'select', this.courseInstanceFilterOptions()),
          this.apiFilterField('category', 'select', this.categoryFilterOptions())
        ];
      case 'payments':
        return [
          externalReference,
          this.apiFilterField('dateFrom', 'date'),
          this.apiFilterField('dateTo', 'date'),
          this.apiFilterField('amountFrom', 'number'),
          this.apiFilterField('amountTo', 'number'),
          this.apiFilterField('paymentType', 'select', this.paymentTypeFilterOptions()),
          this.apiFilterField('studentId', 'select', this.studentFilterOptions()),
          this.apiFilterField('courseInstanceId', 'select', this.courseInstanceFilterOptions()),
          this.apiFilterField('studentQuery', 'text')
        ];
      case 'resources':
        return [
          query,
          externalReference,
          this.apiFilterField('categories', 'select', this.categoryFilterOptions()),
          this.apiFilterField('courseId', 'select', this.courseFilterOptions()),
          this.apiFilterField('courseInstanceId', 'select', this.courseInstanceFilterOptions()),
          this.apiFilterField('instructorId', 'select', this.instructorFilterOptions()),
          this.apiFilterField('availableForCourseInstanceId', 'select', this.courseInstanceFilterOptions())
        ];
      case 'courses':
      case 'organisations':
      case 'categories':
        return [query, externalReference];
      default:
        return [];
    }
  }

  private apiFilterField(
    key: string,
    type: FilterFieldConfig['type'],
    options?: FilterSelectOption[]
  ): FilterFieldConfig {
    return {
      value: key,
      label: this.apiFilterLabel(key),
      type,
      options
    };
  }

  private apiFilterLabel(key: string): string {
    const translated = this.localization.translate(`filter.api.${key}`);
    if (translated !== `filter.api.${key}`) {
      return translated;
    }
    const fallbackMap: Record<string, string> = {
      externalReference: 'field.externalReference',
      query: 'common.search',
      dateFrom: 'filter.api.dateFrom',
      dateTo: 'filter.api.dateTo',
      studentQuery: 'filter.api.studentQuery',
      studentId: 'common.student',
      instructorId: 'common.instructor',
      resourceId: 'common.resource',
      courseInstanceId: 'common.course',
      courseId: 'common.product',
      category: 'common.category',
      categories: 'common.category',
      instructorQuery: 'filter.api.instructorQuery',
      resourceQuery: 'filter.api.resourceQuery',
      courseInstanceQuery: 'filter.api.courseInstanceQuery',
      paymentType: 'paymentDialog.paymentType',
      courseProgress: 'resourceDetails.courseProgress'
    };
    const fallbackKey = fallbackMap[key] ?? `field.${key}`;
    const fallback = this.localization.translate(fallbackKey);
    return fallback === fallbackKey ? key : fallback;
  }

  private studentFilterOptions(): FilterSelectOption[] {
    return this.dataService
      .students()
      .map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private paymentStatusFilterOptions(): FilterSelectOption[] {
    return [
      { value: 'fine', label: this.localization.translate('instructor.overview.healthFine') },
      { value: 'close', label: this.localization.translate('instructor.overview.healthClose') },
      { value: 'exceeded', label: this.localization.translate('instructor.overview.healthExceeded') },
      { value: 'unknown', label: this.localization.translate('common.noData') }
    ];
  }

  private courseProgressFilterOptions(): FilterSelectOption[] {
    return [
      { value: 'pending', label: this.localization.translate('filter.courseProgress.pending') },
      { value: 'in_progress', label: this.localization.translate('filter.courseProgress.inProgress') },
      { value: 'completed', label: this.localization.translate('filter.courseProgress.completed') }
    ];
  }

  private instructorFilterOptions(): FilterSelectOption[] {
    return this.dataService
      .instructors()
      .map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private courseInstanceFilterOptions(): FilterSelectOption[] {
    return this.dataService
      .courseInstances()
      .map((item) => ({ value: item.id, label: item.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private courseFilterOptions(): FilterSelectOption[] {
    return this.dataService
      .courses()
      .map((item) => ({ value: item.id, label: item.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private resourceFilterOptions(): FilterSelectOption[] {
    return this.dataService
      .resources()
      .map((item) => ({ value: item.id, label: item.registration ? `${item.name} (${item.registration})` : item.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private categoryFilterOptions(): FilterSelectOption[] {
    const names = new Set<string>();
    this.dataService.categories().forEach((item) => names.add(item.name));
    this.dataService.courses().forEach((item) => {
      this.asStringArray(item.categories).forEach((category) => names.add(category));
    });
    this.dataService.courseInstances().forEach((item) => {
      this.asStringArray(item.categories ?? item.category).forEach((category) => names.add(category));
    });
    this.dataService.resources().forEach((item) => {
      this.asStringArray(item.categories ?? item.category).forEach((category) => names.add(category));
    });
    return Array.from(names)
      .filter((item) => item.length > 0)
      .map((item) => ({ value: item, label: item }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private paymentTypeFilterOptions(): FilterSelectOption[] {
    return [
      { value: 'cash', label: this.localization.translate('common.paymentType.cash') },
      { value: 'card', label: this.localization.translate('common.paymentType.card') },
      { value: 'transfer', label: this.localization.translate('common.paymentType.transfer') }
    ];
  }

  private lowerFirst(value: string): string {
    if (!value) {
      return value;
    }
    return `${value.charAt(0).toLocaleLowerCase()}${value.slice(1)}`;
  }

  private getLessonFilterOptions(): {
    studentOptions: FilterSelectOption[];
    instructorOptions: FilterSelectOption[];
    categoryOptions: FilterSelectOption[];
  } {
    return {
      studentOptions: this.dataService
        .students()
        .map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      instructorOptions: this.dataService
        .instructors()
        .map((item) => ({ value: item.id, label: `${item.name} ${item.surname}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      categoryOptions: this.dataService
        .categories()
        .map((item) => ({ value: item.name, label: item.name }))
        .sort((a, b) => a.label.localeCompare(b.label))
    };
  }

  private getEmptyLessonFilterOptions(): {
    studentOptions: FilterSelectOption[];
    instructorOptions: FilterSelectOption[];
    categoryOptions: FilterSelectOption[];
  } {
    return {
      studentOptions: [],
      instructorOptions: [],
      categoryOptions: []
    };
  }

  private getLinkedTargetForCell(
    row: TableRow,
    key: string
  ): { entityKey: 'students' | 'instructors' | 'resources'; id: string | number } | null {
    if (key === 'studentId') {
      const id = row[key];
      return id ? { entityKey: 'students', id: id as string | number } : null;
    }

    if (key === 'instructorId') {
      const id = row[key];
      return id ? { entityKey: 'instructors', id: id as string | number } : null;
    }

    const currentEntity = this.entity()?.key;
    if ((key === 'name' || key === 'surname' || key === 'fullName') && (currentEntity === 'students' || currentEntity === 'instructors')) {
      const id = row['id'];
      return id ? { entityKey: currentEntity, id } : null;
    }

    if (key === 'name' && currentEntity === 'resources') {
      const id = row['id'];
      return id ? { entityKey: 'resources', id } : null;
    }

    return null;
  }

  private resolveCourseInstanceCategories(courseInstance: { category?: string; categories?: string[] } | null | undefined): string[] {
    if (!courseInstance) {
      return [];
    }
    if (Array.isArray(courseInstance.categories) && courseInstance.categories.length > 0) {
      return courseInstance.categories;
    }
    return courseInstance.category ? [courseInstance.category] : [];
  }

  private getMonthlyInstructorLessonsBreakdown(row: TableRow): Array<{ category: string; hours: number }> {
    const source = row['monthlyLessonHoursByCategory'];
    if (Array.isArray(source)) {
      return source
        .map((item) => this.asMonthlyLessonBreakdownItem(item))
        .filter((item): item is { category: string; hours: number } => item !== null)
        .sort((a, b) => a.category.localeCompare(b.category));
    }
    if (source && typeof source === 'object') {
      return Object.entries(source as Record<string, unknown>)
        .map(([category, hours]) => ({ category, hours: this.asNumber(hours) }))
        .filter((item) => item.category.trim().length > 0)
        .sort((a, b) => a.category.localeCompare(b.category));
    }
    return [];
  }

  private asMonthlyLessonBreakdownItem(value: unknown): { category: string; hours: number } | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    const row = value as Record<string, unknown>;
    const category = this.firstText(row['category']);
    const hours = this.firstFiniteNumber(row['durationHours']);
    if (!category || hours == null) {
      return null;
    }
    return { category, hours };
  }

  private sameId(left: unknown, right: unknown): boolean {
    return `${left ?? ''}` === `${right ?? ''}`;
  }

  private isCompletedCourseSummary(summary: Record<string, unknown>): boolean {
    const status = this.normalizeStatusToken(summary['status']);
    return ['completed', 'closed', 'finished'].includes(status) || this.asNumber(summary['completedPracticeHours']) >= this.asNumber(summary['requiredPracticeHours']);
  }

  private isActiveCourseSummary(summary: Record<string, unknown>): boolean {
    const status = this.normalizeStatusToken(summary['status']);
    return ['active', 'inProgress', 'inprogress'].includes(status);
  }

  private normalizeStatusToken(value: unknown): string {
    return `${value ?? ''}`.trim().toLowerCase().replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
  }

  private hasId(values: Set<unknown>, id: unknown): boolean {
    return Array.from(values).some((value) => this.sameId(value, id));
  }
}
